import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { DocumentsService } from '../../../js/api';
import { handleApiError } from '../../../js/app/utils/errorhandling';

export type DocumentDisposition = 'inline' | 'attachment';

/**
 * Open (inline) or download (attachment) a document through its presigned URL.
 *
 * Safari (iOS especially) only allows window.open() in direct response to a user
 * gesture — calling it inside an async onSuccess silently no-ops. So for inline we
 * open a blank placeholder synchronously during the click and navigate it once the
 * URL is ready; for attachment we navigate the current window, since
 * Content-Disposition: attachment downloads without leaving the page.
 * Mirrors the documents tab's handler so cards elsewhere behave the same way.
 */
export function useOpenDocument(messagePrefix = 'Failed to open the document') {
  const mutation = useMutation({
    mutationFn: ({ id, disposition }: { id: string; disposition: DocumentDisposition }) =>
      DocumentsService.documentsDownloadRetrieve({ id, disposition }),
    onError: (error: Error) => handleApiError(error, { messagePrefix, handle403: true }),
  });

  const openDocument = (id: string, disposition: DocumentDisposition = 'inline') => {
    const placeholder = disposition === 'inline' ? window.open('', '_blank') : null;
    mutation.mutate(
      { id, disposition },
      {
        onSuccess: (result) => {
          if (!result?.url) {
            placeholder?.close();
            toast.error('Download URL not available');
            return;
          }
          if (placeholder && !placeholder.closed) {
            placeholder.location.href = result.url;
          } else {
            window.location.href = result.url;
          }
        },
        onError: () => placeholder?.close(),
      }
    );
  };

  return { openDocument, isPending: mutation.isPending };
}
